import { useState } from "react";
import { ErrorReason } from "@/briefpoint-client";
import { toast } from "@/components/ui/toast";
import styles from "./useBatchErrorToasts.module.scss";

const ToastContent = (props: { message: string; filenames: string[] }) => {
  const [showAll, setShowAll] = useState(false);

  return (
    <div>
      <div>
        {props.message}
        <ul className={styles.errorList}>
          {props.filenames.slice(0, showAll ? undefined : 5).map((filename) => (
            <li key={filename}>{filename}</li>
          ))}
        </ul>

        {props.filenames.length > 5 && (
          <button
            className={styles.showAllButton}
            onClick={() => setShowAll(!showAll)}
            type="button"
          >
            {showAll ? "Show Less" : "Show All"}
          </button>
        )}
      </div>
    </div>
  );
};

export default function useBatchErrorToasts() {
  return (errorResults: Record<string, ErrorReason>) => {
    const errors = Object.entries(errorResults).reduce<{
      unsupportedFileTypes: string[];
      emptyFiles: string[];
      invalidSignatures: string[];
      fileHashMismatches: string[];
      duplicates: string[];
    }>(
      (acc, [filename, errorReason]: [string, ErrorReason]) => {
        if (errorReason === ErrorReason.UnsupportedFileType) {
          acc.unsupportedFileTypes.push(filename);
        } else if (errorReason === ErrorReason.EmptyFile) {
          acc.emptyFiles.push(filename);
        } else if (errorReason === ErrorReason.InvalidSignature) {
          acc.invalidSignatures.push(filename);
        } else if (errorReason === ErrorReason.FileHashMismatch) {
          acc.fileHashMismatches.push(filename);
        } else if (errorReason === ErrorReason.Duplicate) {
          acc.duplicates.push(filename);
        }
        return acc;
      },
      {
        unsupportedFileTypes: [],
        emptyFiles: [],
        invalidSignatures: [],
        fileHashMismatches: [],
        duplicates: [],
      },
    );

    if (errors.duplicates.length > 0) {
      toast.warning(
        <ToastContent
          message="Some files were not uploaded because duplicates were found."
          filenames={errors.duplicates}
        />,
      );
    }

    if (errors.unsupportedFileTypes.length > 0) {
      toast.warning(
        <ToastContent
          message="Some files were not uploaded because their file types are not supported."
          filenames={errors.unsupportedFileTypes}
        />,
      );
    }

    if (errors.emptyFiles.length > 0) {
      toast.warning(
        <ToastContent
          message="Some files were not uploaded because they are empty."
          filenames={errors.emptyFiles}
        />,
      );
    }

    if (errors.invalidSignatures.length > 0) {
      toast.warning(
        <ToastContent
          message="Some files were not uploaded because they have invalid signatures."
          filenames={errors.invalidSignatures}
        />,
      );
    }

    if (errors.fileHashMismatches.length > 0) {
      toast.warning(
        <ToastContent
          message="Some files were not uploaded because they have mismatched file hashes."
          filenames={errors.fileHashMismatches}
        />,
      );
    }
  };
}
